import { StartAmPm } from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionTermDateV2';
import React, { SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';

const TimeContainer = styled.div<{
  $positionTop: string;
  $usePosition: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
  max-height: 194px;

  z-index: 10;

  ${({ $usePosition, $positionTop }) =>
    $usePosition
      ? `
      position: absolute;
      top: ${$positionTop};
    `
      : `
      position: relative;
      margin-top: 16px;
    `}
`;

const TimePickerContainer = styled.div<{
  $usePosition: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #eaebec;

  background: #fff;

  ${({ $usePosition }) =>
    $usePosition
      ? `
      box-shadow: 0px 4px 20px 0px rgba(65, 75, 86, 0.15),
    0px 8px 10px 0px rgba(65, 75, 86, 0.15);
    `
      : `

    `}
`;

const TimePickerBody = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-around;
`;

const TimeInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  max-height: 190px;
  overflow: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TimeInputItem = styled.div<{ $isOverDay: boolean; $isSelected: boolean }>`
  padding: ${props => (props.$isOverDay ? '8px 12px' : '8px 24px')};
  border-radius: 8px;
  color: ${props => (props.$isSelected ? '#0071E9' : '#414B56')};
  background: ${props => (props.$isSelected ? '#F4F4F5' : '#fff')};
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
`;

const AmPmSelector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AmPmOption = styled.div<{ $isSelected: boolean; $isOverDay: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  padding: ${props => (props.$isOverDay ? '8px 12px' : '8px 24px')};
  border-radius: 8px;
  color: ${props => (props.$isSelected ? '#0071E9' : '#414B56')};
  background: ${props => (props.$isSelected ? '#F4F4F5' : '#fff')};
  white-space: nowrap;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const TIME_SELECTOR_DATA = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
];

const MINUTE_SELECTOR_DATA = [
  '00',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
  '31',
  '32',
  '33',
  '34',
  '35',
  '36',
  '37',
  '38',
  '39',
  '40',
  '41',
  '42',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '50',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '58',
  '59',
];

interface Props {
  isOverDay: boolean;
  onClose: () => void;
  handleSetTermDate: (value: string, amPm: StartAmPm) => void;
  positionTop?: string;
  usePosition?: boolean;
}

const TimeSelector: React.FC<Props> = props => {
  const {
    isOverDay,
    onClose,
    handleSetTermDate,
    positionTop = '55px',
    usePosition = true,
  } = props;

  const [startHour, setStartHour] = useState<string>('0');
  const [startMinute, setStartMinute] = useState<string>('0');
  const [startAmPm, setStartAmPm] = useState<StartAmPm>(StartAmPm.AM);

  const handleAcceptTermDate = () => {
    const hour =
      startAmPm === 'am'
        ? startHour === '12'
          ? '00'
          : startHour
        : startHour === '12'
        ? '12'
        : (Number(startHour) + 12).toString();

    handleSetTermDate(`${hour}:${startMinute}`, startAmPm);
  };

  useEffect(() => {
    if (startHour !== '0' && startMinute !== '0') {
      onClose();
      handleAcceptTermDate();
    }
  }, [startHour, startMinute, onClose, handleSetTermDate]);

  return (
    <TimeContainer $positionTop={positionTop} $usePosition={usePosition}>
      <TimePickerContainer $usePosition={usePosition}>
        <TimePickerBody>
          <AmPmSelector>
            <AmPmOption
              $isOverDay={isOverDay}
              $isSelected={startAmPm === StartAmPm.AM}
              onClick={() => setStartAmPm(StartAmPm.AM)}
            >
              오전
            </AmPmOption>
            <AmPmOption
              $isOverDay={isOverDay}
              $isSelected={startAmPm === StartAmPm.PM}
              onClick={() => setStartAmPm(StartAmPm.PM)}
            >
              오후
            </AmPmOption>
          </AmPmSelector>

          <TimeInputContainer>
            {TIME_SELECTOR_DATA.map(time => (
              <TimeInputItem
                key={time}
                $isOverDay={isOverDay}
                $isSelected={startHour === time}
                onClick={() => setStartHour(time)}
              >
                {time}시
              </TimeInputItem>
            ))}
          </TimeInputContainer>

          <TimeInputContainer>
            {MINUTE_SELECTOR_DATA.map(time => (
              <TimeInputItem
                key={time}
                $isOverDay={isOverDay}
                $isSelected={startMinute === time}
                onClick={() => setStartMinute(time)}
              >
                {time}분
              </TimeInputItem>
            ))}
          </TimeInputContainer>
        </TimePickerBody>
      </TimePickerContainer>
    </TimeContainer>
  );
};

export default TimeSelector;
